import React, { useState, useEffect } from 'react';
import { ticketsAPI } from '../../services/api';
import { Button } from '../ui';
import { handleApiError, showToast } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { X } from 'lucide-react';

const TicketFormModal = ({ ticket, onClose, onSuccess }) => {
  const { isRTL, t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'other',
    priority: 'medium',
  });

  useEffect(() => {
    if (ticket) {
      setFormData({
        subject: ticket.subject || '',
        description: ticket.description || '',
        category: ticket.category || 'other',
        priority: ticket.priority || 'medium',
      });
    }
  }, [ticket]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.description.trim()) {
      showToast.error(t('requiredFields'));
      return;
    }

    try {
      setLoading(true);

      if (ticket) {
        // Update existing ticket
        await ticketsAPI.update(ticket.id, formData);
        showToast.success(t('ticketUpdatedSuccess'));
      } else {
        // Create new ticket
        await ticketsAPI.create(formData);
        showToast.success(t('ticketCreatedSuccess'));
      }

      onSuccess();
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-2xl font-bold text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
              {ticket ? t('editTicket') : t('newTicket')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Subject */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('ticketSubject')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 ${isRTL ? 'text-right' : 'text-left'}`}
                placeholder={t('ticketSubjectPlaceholder')}
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('ticketDescription')} <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={6}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 ${isRTL ? 'text-right' : 'text-left'}`}
                placeholder={t('ticketDescriptionPlaceholder')}
                required
              />
            </div>

            {/* Category & Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('ticketCategory')}
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <option value="technical">{t('categoryTechnical')}</option>
                  <option value="billing">{t('categoryBilling')}</option>
                  <option value="feature_request">{t('categoryFeatureRequest')}</option>
                  <option value="bug">{t('categoryBug')}</option>
                  <option value="other">{t('categoryOther')}</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('ticketPriority')}
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <option value="low">{t('priorityLow')}</option>
                  <option value="medium">{t('priorityMedium')}</option>
                  <option value="high">{t('priorityHigh')}</option>
                  <option value="urgent">{t('priorityUrgent')}</option>
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={loading}
                className="flex-1"
              >
                {ticket ? t('saveChanges') : t('createTicket')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={loading}
                className="flex-1"
              >
                {t('cancelButton')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TicketFormModal;
